<!-- Copyright (c) 2024 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="print-jobcharges-main">
    <div class="jobcharges-options">
      <div class="job-filters-and-table-box-controls">
        <div class="job-filters-summary-container">
          <span>{{ jobFiltersSummary }}</span>
        </div>
        <ButtonFilter
          buttonText="Change Filter"
          filterPosition="right"
          :filterWidth="420"
          :isOpen.sync="jobFiltersIsOpen"
        >
          <div class="button-filter-content-container">
            <BoxContainer
              title="Job Date"
              :isCheckBox="true"
              :isChecked.sync="jobDateIsChecked"
              @checkBoxClicked="checkJobDate"
            >
              <DateTimePicker
                placeholder="Start Job Date "
                :value.sync="jobDateStartValue"
                :showTime="false"
                :isRequired="jobDateIsChecked"
                :errorMessage.sync="jobDateStartErrorMessage"
                :isDisabled="!jobDateIsChecked"
              ></DateTimePicker>
              <DateTimePicker
                placeholder="End Job Date "
                :value.sync="jobDateEndValue"
                :showTime="false"
                :isRequired="jobDateIsChecked"
                :errorMessage.sync="jobDateEndErrorMessage"
                :isDisabled="!jobDateIsChecked"
              ></DateTimePicker>
            </BoxContainer>
            <BoxContainer
              class="jobno-filter-container"
              title="Job No."
              :isCheckBox="true"
              :isChecked.sync="jobnoIsChecked"
              @checkBoxClicked="checkJobNo"
            >
              <InputQuery
                placeholder="Start Job No."
                :items="jobnoItems"
                itemKey="Job No"
                :showDesc="false"
                :selectedObject.sync="jobnoStartSelectedObject"
                :search.sync="jobnoStartSearch"
                :isRequired="jobnoIsChecked"
                :errorMessage.sync="jobnoStartErrorMessage"
                :isDisabled="!jobnoIsChecked"
              />
              <InputQuery
                placeholder="End Job No."
                :items="jobnoItems"
                itemKey="Job No"
                :showDesc="false"
                :selectedObject.sync="jobnoEndSelectedObject"
                :search.sync="jobnoEndSearch"
                :isRequired="jobnoIsChecked"
                :errorMessage.sync="jobnoEndErrorMessage"
                :isDisabled="!jobnoIsChecked"
              />
            </BoxContainer>
            <div class="refresh-button-container">
              <button @click="refreshClicked">Refresh</button>
            </div>
          </div>
        </ButtonFilter>
        <button
          class="select-deselect-all-button"
          :isDisabled="selectDeselectAllButtonIsDisabled"
          @click="selectDeselectAllClicked"
        >
          {{ selectDeselectAllButtonText }}
        </button>
      </div>
      <div class="job-table-box-container">
        <TableBox
          title="Job Charges List"
          :tableData.sync="jobTableData"
          :selectedRow.sync="jobSelectedRow"
          :maxSelection="jobTableData.length"
          :dataType="jobDataType"
          :loading="jobLoading"
          :columnWidth="[100, 130, 150, 150, 100, 200]"
        ></TableBox>
      </div>
      <div class="header-formatting-controls">
        <div class="header-formatting-column">
          <CheckBoxV2 title="Show Company Letter Head" :isChecked.sync="showCompanyLetterHeadIsChecked" />
          <CheckBoxV2 title="Show Job Header Info" :isChecked.sync="showJobHeaderInfoIsChecked" />
        </div>
        <div class="header-formatting-column">
          <CheckBoxV2
            v-if="showInternalCopyOnlyCheckbox"
            title="Internal Copy Only"
            :isChecked.sync="internalCopyOnlyIsChecked"
          />
        </div>
      </div>
      <div class="call-to-action-buttons">
        <button @click="previewJobClicked">Preview Job</button>
      </div>
    </div>
    <div class="job-preview">
      <DesignerV3
        v-if="showDesignerV3"
        :reportFiles="designerV3ReportFiles"
        :reportData="designerV3ReportData"
        :sharedData="designerV3SharedData"
        :canToggleBackground="false"
        ref="designerV3"
        :canShare="true"
        :shareBehaviour="designerV3ShareBehaviour"
        :canClose="false"
        @shareClicked="designerV3ShareClicked"
      />
      <div v-else class="user-instructions">
        <span>Select some job and click on <strong>Preview Job</strong> to get started</span>
      </div>
    </div>
    <PopupV2
      class="email-job-popup"
      :show="showEmailJobPopup"
      icon="https://dsmlib.logistics.systems/Icons/BlackMail.png"
      :buttons="emailJobPopupButtons"
      title="Email Job Charges List"
      width="60%"
      @buttonClicked="emailJobPopupButtonClicked"
    >
      <div class="email-job-popup-content-container">
        <div class="mail-inputs-container">
          <div class="email-input-container">
            <EmailInput :mails.sync="emailJobPopupToMails" title="To" />
          </div>
          <div class="email-input-container">
            <EmailInput :mails.sync="emailJobPopupCCMails" title="CC">
              <span class="note">Your email will be automatically included in the CC</span>
            </EmailInput>
          </div>
          <div class="email-input-container">
            <EmailInput :mails.sync="emailJobPopupBCCMails" title="BCC" />
          </div>
        </div>
        <div class="email-content-inputs-container">
          <Input :inputValue.sync="emailJobPopupSubjectInputValue" placeholder="Subject" validator="text,100" />
          <RemarksBox title="Message" :text.sync="emailJobPopupMessageText" :maxLength="1000" />
        </div>
      </div>
    </PopupV2>
  </div>
</template>

<script>
/* eslint-disable */

export default {
  components: {},
  computed: {
    selectDeselectAllButtonIsDisabled: function () {
      return this.jobTableData.length == 0;
    },

    selectDeselectAllButtonText: function () {
      if (this.jobTableData.length == 0) {
        return 'Select All';
      }
      return this.jobTableData.length != this.jobSelectedRow.length ? 'Select All' : 'Deselect All';
    },
  },
  data: function () {
    return {
      userDetails: null,

      //#region job Filters
      jobFiltersSummary: '',
      jobFiltersIsOpen: false,

      jobDateIsChecked: false,
      jobDateStartValue: null,
      jobDateStartErrorMessage: '',
      jobDateEndValue: null,
      jobDateEndErrorMessage: '',

      jobnoIsChecked: false,
      jobnoItems: [],
      jobnoStartSelectedObject: null,
      jobnoStartSearch: '',
      jobnoStartErrorMessage: '',
      jobnoEndSelectedObject: null,
      jobnoEndSearch: '',
      jobnoEndErrorMessage: '',
      //#endregion

      //#region job table box
      jobTableData: [],
      jobSelectedRow: [],
      jobDataType: { '#': 'number', 'No. of Job Charges': 'number' },
      jobLoading: false,
      //#endregion

      showCompanyLetterHeadIsChecked: false,
      showJobHeaderInfoIsChecked: false,

      showInternalCopyOnlyCheckbox: false,
      internalCopyOnlyIsChecked: false,

      totalSellingTax: 0,
      totalBuyingTax: 0,
      totalSellingFAmt: 0,
      totalBuyingFAmt: 0,

      //#region designerv3
      showDesignerV3: false,
      designerV3ReportFiles: [],
      designerV3ReportData: [],
      designerV3SharedData: [],
      designerV3ShareBehaviour: {
        behaviour: 'uploadAndEmit',
        options: {
          fileName: '',
          folder: '',
        },
      },
      //#endregion

      //#region email job charges list
      showEmailJobPopup: false,
      emailJobPopupButtons: [{ text: 'Send Email' }, { text: 'Cancel', color: 'lightgray' }],
      emailJobPopupToMails: [],
      emailJobPopupCCMails: [],
      emailJobPopupBCCMails: [],
      emailJobPopupSubjectInputValue: '',
      emailJobPopupMessageText: '',
      //#endregion
    };
  },
  props: {},
  methods: {
    refreshClicked: async function (pointerEvent) {
      let error = false;

      if (this.jobDateIsChecked) {
        if (!this.jobDateStartValue) {
          this.jobDateStartErrorMessage = 'Start Job Date is required!';
          error = true;
        }

        if (!this.jobDateEndValue) {
          this.jobDateEndErrorMessage = 'End Job Date is required!';
          error = true;
        }
      }

      if (this.jobnoIsChecked) {
        if (!this.jobnoStartSelectedObject && !this.jobnoStartSearch.trim()) {
          this.jobnoStartErrorMessage = 'Start Job No. is required!';
          error = true;
        }

        if (!this.jobnoEndSelectedObject && !this.jobnoEndSearch.trim()) {
          this.jobnoEndErrorMessage = 'End Job No. is required!';
          error = true;
        }
      }

      if (error) return;

      if (!this.jobDateIsChecked && !this.jobnoIsChecked) {
        const proceedConfirmation = await this.$showAlert(
          'warning',
          'This May Take A While',
          'You are not filtering by Job Date and Job No.\n\nLoading this data may take a while. Proceed?',
          [{ text: 'Proceed' }, { text: 'Cancel', color: 'lightgray' }],
        );

        if (proceedConfirmation === 'Cancel') return;
      }

      this.jobFiltersIsOpen = false;
      this.updateJobFilterSummary();
      await this.getFilteredJobList();
    },

    updateJobFilterSummary: function () {
      let filtersList = [];

      if (this.jobDateIsChecked) {
        filtersList.push(
          `date from ${this.jobDateStartValue.formatString('yyyy-MM-dd')} to ${this.jobDateEndValue.formatString('yyyy-MM-dd')}`,
        );
      }

      if (this.jobnoIsChecked) {
        let jobnoFilterSummary = 'Job No. from ';

        if (this.jobnoStartSelectedObject) {
          jobnoFilterSummary += `${this.jobnoStartSelectedObject['Job No']} to `;
        } else {
          jobnoFilterSummary += `${this.jobnoStartSearch} to `;
        }

        if (this.jobnoEndSelectedObject) {
          jobnoFilterSummary += `${this.jobnoEndSelectedObject['Job No']}`;
        } else {
          jobnoFilterSummary += `${this.jobnoEndSearch}`;
        }

        filtersList.push(jobnoFilterSummary);
      }

      this.jobFiltersSummary = `Filter by ${filtersList.join(', ')}`;
    },

    getFilteredJobList: async function () {
      let requestBody = {
        startdate: this.jobDateIsChecked ? this.jobDateStartValue.formatString('yyyy-MM-dd') : '',
        enddate: this.jobDateIsChecked ? this.jobDateEndValue.formatString('yyyy-MM-dd') : '',
        startjobno: this.jobnoIsChecked ? (this.jobnoStartSelectedObject?.['Job No'] ?? jobnoStartSearch) : '',
        endjobno: this.jobnoIsChecked ? (this.jobnoEndSelectedObject?.['Job No'] ?? this.jobnoEndSearch) : '',
      };
      this.jobTableData = [];
      this.jobSelectedRow = [];

      this.jobLoading = true;
      let jobListing = await this.$apiCallV2('/PrintJobCharges/PrintJobChargesFilter', requestBody);
      this.jobTableData = jobListing.filter((job) => job.Indicator !== 'C');
      //select if only one row
      if (this.jobTableData.length === 1) {
        this.$nextTick(() => {
          this.jobSelectedRow = [...this.jobTableData];
        });
      }
      this.jobLoading = false;
    },

    selectDeselectAllClicked: function (pointerEvent) {
      if (this.jobTableData.length !== this.jobSelectedRow.length) {
        this.jobSelectedRow = [];
        this.$nextTick(() => {
          this.jobSelectedRow = [...this.jobTableData];
        });
      } else {
        this.jobSelectedRow = [];
      }
    },

    previewJobClicked: async function (pointerEvent) {
      if (this.jobSelectedRow.length == 0) {
        this.$showAlert('error', 'No Job Selected', 'Please select at least one job to preview', [{ text: 'OK' }]);
        return;
      }

      const requestBody = {
        jobarray: this.jobSelectedRow.map((jobSelectedRow) => jobSelectedRow['Job No']).join(','),
        showcompanyletterhead: this.showCompanyLetterHeadIsChecked ? '1' : '0',
        showheaderinfo: this.showJobHeaderInfoIsChecked ? '1' : '0',
        company: this.userDetails.CoName,
        coshortname: this.userDetails.CoRefname,
      };

      this.$fullscreenLoading('Getting Job Charges List');
      let jobDetails = await this.$apiCallV2('/PrintJobCharges/PrintJobChargesList', requestBody);
      if (Array.isArray(jobDetails) && jobDetails[0].Results === '1') {
        this.$showAlert('error', 'Error Printing Job Charges', jobDetails[0].ErrMsg, [{ text: 'OK' }]);
        this.$fullscreenLoading();
        return;
      }
      this.$fullscreenLoading();
      jobDetails = jobDetails.PrintJob;

      // these are needed for any format
      jobDetails.General['Show Company Letter Head'] = jobDetails.General['Show Company Letter Head'] !== '0';
      jobDetails.General['companyheader'] = this.userDetails['CoHeaderFin'] ? this.userDetails['CoHeaderFin'] : jobDetails.General['companyheader'];
      jobDetails.General['Show Header Info'] = jobDetails.General['Show Header Info'] !== '0';
      jobDetails.General._companyName = this.userDetails.CoName;
      jobDetails.General.companyfooter = this.userDetails.CoFooterFin;

      this.$fullscreenLoading('Preparing to Print');
      for (let job of jobDetails.JobHeader) {
        this.totalCalc(job);
        job.totalSelling = this.formatNumber(job.totalSelling);
        job.totalFNet_amt = this.totalSellingFAmt == 0 ? 'N/A' : this.formatNumber(this.totalSellingFAmt);
        job.totalSellingTax = this.formatNumber(this.totalSellingTax);
        job.totalFAmount = this.totalBuyingFAmt == 0 ? 'N/A' : this.formatNumber(this.totalBuyingFAmt);
        job.totalBuying = this.formatNumber(job.totalBuying);
        job.totalBuyingTax = this.formatNumber(this.totalBuyingTax);
        job.totalProfit = this.formatNumber(job.totalProfit);
        job.totalProfitMargin = parseFloat(job.totalProfitMargin).toFixed(2);
        job.username = this.userDetails.UserName;
        for (let det of job.JobDetails) {
          job.shipper = det.shipper;
          job.consignee = det.consignee;
          job.vesselTitle = det.vesselTitle;
          job.vesselNo = det.vesselNo;
          job.voyTitle = det.voyTitle;
          job.voyNo = det.voyNo;
          job.Load_port = det.Load_port;
          job.Discharge_port = det.Discharge_port;
          job.typeTitle = det.typeTitle;
          job.typeNo = det.typeNo;
          job.dateTitle = det.dateTitle;
          job.typeDate = det.typeDate;
          job.hblTitle = det.hblTitle;
          job.hblNo = det.hblNo;
          job.blTitle = det.blTitle;
          job.blNo = det.blNo;
          job.noOfPkgs = det.noOfPkgs;
          job.packingType = det.packingType;
          job.commodity = det.commodity;
          job.salesman = det.salesman;
          job.GWeight = det.GWeight;
          job.M3 = det.M3;
          job.chargeableWt = det.Nweight > job.GWeight ? job.M3 + ' M3' : job.GWeight + ' KG';
        }
      }

      const jobDet = jobDetails.JobHeader ? this.formatDecimal(jobDetails.JobHeader) : [];

      jobDetails.JobHeader.forEach((header, index) => {
        if (jobDet[header.jobno]) {
          jobDetails.JobHeader[index].JobChargesList = jobDet[header.jobno];
        }
      });

      jobDetails.JobHeader.totalSellingTax = this.totalSellingTax;
      jobDetails.JobHeader.totalBuyingTax = this.totalBuyingTax;
      this.$fullscreenLoading();

      this.designerV3ReportFiles = Array.from({ length: jobDetails.JobHeader.length }, () => 'JobChargesList.json');
      this.designerV3ReportData = jobDetails.JobHeader;
      this.designerV3SharedData = jobDetails.General;
      this.showDesignerV3 = true;
    },

    totalCalc: function (jobs) {
      let sellingCurr = new Set();
      let buyingCurr = new Set();
      this.totalSellingTax = 0;
      this.totalSellingFAmt = 0;
      this.totalBuyingFAmt = 0;
      this.totalBuyingTax = 0;
      if (jobs.JobChargesList != undefined) {
        for (const j of jobs.JobChargesList) {
          sellingCurr.add(j.currency);
          buyingCurr.add(j.Fcurrency);
          this.totalSellingFAmt += parseFloat(j.FNet_amt);
          this.totalBuyingFAmt += parseFloat(j.FAmount);
          this.totalSellingTax += parseFloat((j.Net_amt * j.srvtaxrate) / 100);
          this.totalBuyingTax += parseFloat((j.Amount * j.buyingtax) / 100);
        }
        if (sellingCurr.size > 1) this.totalSellingFAmt = 0;

        if (buyingCurr.size > 1) this.totalBuyingFAmt = 0;
      }
    },

    formatDecimal: function (jobs) {
      let data = {};
      for (const i of jobs) {
        if (i.JobChargesList != undefined) {
          for (const j of i.JobChargesList) {
            let item = {};
            item = {
              jobno: j.jobno,
              seq: j.seq,
              ChargesCode: j.ChargesCode,
              Description: j.Description,
              contno: j.contno,
              billcu: j.billcu,
              currency: j.currency,
              Qty: j.Qty,
              FNet_amt: this.formatNumber(j.FNet_amt),
              rate: j.rate,
              Net_amt: this.formatNumber(j.Net_amt),
              taxable_amount: this.formatNumber((j.Net_amt * j.srvtaxrate) / 100),
              Fcurrency: j.Fcurrency,
              Frate: j.Frate,
              FAmount: this.formatNumber(j.FAmount),
              Amount: this.formatNumber(j.Amount),
              tax: this.formatNumber((j.Amount * j.buyingtax) / 100),
              profit: this.formatNumber(j.profit),
            };
            if (!data[j.jobno]) {
              data[j.jobno] = [];
            }
            data[j.jobno].push(item);
          }
        }
      }
      return data;
    },

    formatNumber: function (num) {
      if (Number(num) < 0) {
        let formattedVal = `(${this.$currencyFormat(Number(num) * -1)})`;
        return formattedVal;
      }
      return this.$currencyFormat(num);
    },

    checkJobDate: function (check) {
      if (!check) {
        this.jobDateStartErrorMessage = '';
        this.jobDateEndErrorMessage = '';
      }
    },

    checkJobNo: function (check) {
      if (!check) {
        this.jobnoStartErrorMessage = '';
        this.jobnoEndErrorMessage = '';
      }
    },

    designerV3ShareClicked: function (shareLink) {
      if (this.designerV3ReportData[0].cu_email) {
        this.emailJobPopupToMails = this.designerV3ReportData[0].cu_email.split(';').map((email) => ({
          email: email.trim(),
          valid: true,
        }));
      } else {
        this.emailJobPopupToMails = [];
      }

      this.emailJobPopupCCMails = [];
      this.emailJobPopupBCCMails = [];

      if (this.designerV3ReportData.length > 1) {
        this.emailJobPopupSubjectInputValue = 'Job Charges List';
        this.emailJobPopupMessageText = `Job Charges List: ${shareLink}\n\n`;

        for (const jobData of this.designerV3ReportData) {
          this.emailJobPopupMessageText += `Job Charges List ${jobData.jobno}\n`;
        }
      } else {
        this.emailJobPopupSubjectInputValue = `Job Charges List ${this.designerV3ReportData[0].jobno}`;
        this.emailJobPopupMessageText = `Job Charges List: ${shareLink}\n`;
      }

      this.showEmailJobPopup = true;
    },

    emailJobPopupButtonClicked: async function (buttonText) {
      if (buttonText === 'Cancel') {
        this.showEmailJobPopup = false;
        return;
      }

      if (this.emailJobPopupToMails.filter((mail) => mail.valid).length === 0) {
        this.$showAlert('error', 'No Valid Emails', 'Please enter at least one valid To email', [{ text: 'OK' }]);
        return;
      }

      if (!this.emailJobPopupSubjectInputValue.trim()) {
        const proceedWithoutSubject = await this.$showAlert(
          'warning',
          'No Subject',
          'No subject was entered\nProceed to email without a subject?',
          [{ text: 'Proceed' }, { text: 'Cancel', color: 'lightgray' }],
        );
        if (proceedWithoutSubject === 'Cancel') {
          return;
        }
      }

      const requestBody = {
        emaillist: [
          {
            doctype: 'JobChargeList',
            docno: this.designerV3ReportData.map((jobData) => jobData.jobno).join(','),
            docnofieldname: '',
            recipient: this.emailJobPopupToMails.map((mail) => mail.email).join(';'),
            cc: this.emailJobPopupCCMails
              .map((mail) => mail.email)
              .concat([this.userDetails.Email])
              .join(';'),
            bcc: this.emailJobPopupBCCMails.map((mail) => mail.email).join(';'),
            subject: this.emailJobPopupSubjectInputValue.trim(),
            message:
              this.emailJobPopupMessageText + `\nSender: ${this.userDetails.UserName} (${this.userDetails.Email})`,
          },
        ],
      };

      this.$fullscreenLoading('Sending Email');
      const sendEmailResponse = await this.$apiCallV2('/PrintJobCharges/SendEmail', requestBody);
      this.$fullscreenLoading();

      if (sendEmailResponse?.trim?.()?.endsWith?.('queue to send.')) {
        this.$showAlert('success', 'Email successfully sent', 'Your email has been successfully sent', [
          { text: 'OK' },
        ]);
        this.showEmailJobPopup = false;
      } else {
        this.$showAlert('error', 'Email not sent', 'Your email could not be sent. Please try again.', [{ text: 'OK' }]);
      }
    },
  },

  async mounted() {
    const mountedPromises = [];
    let promisesFulfilled = 0;
    const currentDate = new Date();
    let startDate = new Date(currentDate);
    startDate.setDate(-180);
    startDate = startDate.formatString('yyyy-MM-dd');
    const endDate = currentDate.formatString('yyyy-MM-dd');
    const dataToPost = {
      startdate: startDate,
      enddate: endDate,
    };
    mountedPromises.push(
      this.$apiCallV2(`/PrintJobCharges/PrintJobChargesFilter`, dataToPost).then((result) => {
        this.jobnoItems = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=LetterHead_JC').then((result) => {
        const letterHead = result[0].SettingsValue;
        if (letterHead) {
          this.showCompanyLetterHeadIsChecked = letterHead.toUpperCase() == 'TRUE' ? true : false;
        }
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=JobChargesInfo').then((result) => {
        const headerInfo = result[0].SettingsValue;
        if (headerInfo) {
          this.showJobHeaderInfoIsChecked = headerInfo.toUpperCase() == 'TRUE' ? true : false;
        }
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Login/GetDetails').then((result) => {
        this.userDetails = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    await Promise.allSettled(mountedPromises);

    if (this.$route.query.jobno) {
      this.jobnoIsChecked = true;

      const queryParamJobNo = this.$route.query.jobno.trim();
      const matchingJob = this.jobnoItems.find((job) => job['Job No'] === queryParamJobNo);

      if (matchingJob) {
        this.jobnoStartSelectedObject = matchingJob;
        this.jobnoEndSelectedObject = matchingJob;
      } else {
        this.jobnoStartSearch = queryParamJobNo;
        this.jobnoEndSearch = queryParamJobNo;
      }
    } else {
      this.jobDateIsChecked = true;
      this.jobDateStartValue = new Date();
      this.jobDateEndValue = new Date();
    }

    this.updateJobFilterSummary();
    this.getFilteredJobList();
  },
  watch: {},
};
</script>
<style lang="scss" scoped>
@import '@/css/all.scss';

.print-jobcharges-main {
  font-size: 12px;
  width: 100%;
  height: 100%;
  padding: 10px 45px;
  display: flex;

  > .jobcharges-options {
    margin-right: 2.5px;
    flex: 1;
    background-color: white;
    padding: 5px;

    > .job-filters-and-table-box-controls {
      display: flex;
      flex-direction: row;
      justify-content: flex-end;
      margin-bottom: 5px;

      > .job-filters-summary-container {
        margin-right: 5px;
        align-content: center; // vertically align the summary text
        text-align: end; // horizontally align the summary text
      }

      .button-filter-content-container {
        text-align: left;
        cursor: auto;

        .jobno-filter-container {
          margin-top: 15px;
        }

        .refresh-button-container {
          margin-top: 10px;
          display: flex;
          justify-content: flex-end;
        }
      }

      > .select-deselect-all-button {
        margin-left: 5px;
      }
    }

    > .job-table-box-container {
      height: calc(100% - 345px);
    }

    > .header-formatting-controls {
      display: flex;
      flex-direction: row;
      margin-top: 5px;

      > .header-formatting-column {
        width: 50%;
      }
    }

    > .call-to-action-buttons {
      display: flex;
      flex-direction: row;
      justify-content: flex-end;
    }
  }

  > .job-preview {
    margin-left: 2.5px;
    flex: 1;
    background-color: white;
    padding: 5px;

    > .user-instructions {
      height: 100%;
      display: flex;
      text-align: center;
      justify-content: center;
      align-items: center;
      font-size: 20px;
    }
  }

  > .email-job-popup {
    .email-job-popup-content-container {
      display: flex;
      flex-direction: row;

      > .mail-inputs-container {
        margin-right: 5px;
        display: flex;
        flex-direction: column;
        flex: 1;

        > .email-input-container {
          margin-bottom: 5px;
        }
      }

      > .email-content-inputs-container {
        margin-left: 5px;
        display: flex;
        flex-direction: column;
        flex: 2;
      }
    }
  }
}
</style>
